import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createBlockResponse,
  createCriterionResponse,
  getRun,
  listBlockResponses,
  listCriterionResponses,
  listInstrumentBlocks,
  listInstrumentCriteria,
  listMyAssignedRuns,
  listScaleAnchors,
  submitSelfAssessment,
  updateBlockResponse,
  updateCriterionResponse,
  type ScaleLevel,
} from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { NOT_APPLICABLE, UNABLE_TO_ASSESS } from "../lib/pmoReport";
import { areaAfterCompleting, currentAssessment, isAnswering, isSubmitted, progress, resumeAreaIndex } from "../lib/respondent";
import { offersReason, storedReason } from "../lib/answerReason";
import type {
  BlockResponse,
  CriterionResponse,
  InstrumentBlock,
  InstrumentCriterion,
  Run,
} from "../lib/types";

// The questionnaire — decision §6 step 4, with the Business Architect's
// C1–C6 ruling (RULING_BUSINESS_ARCHITECT_PMO_SELF_ASSESSMENT_C1_C6 240926):
//
//   - four answers on the instrument's own scale;
//   - "Unable to assess" (C3) — recorded as `not_assessed`, no value, never a
//     zero, and distinct from a question not yet answered;
//   - "Not applicable" (C1.1) — `not_applicable` with the respondent's own
//     justification, for a question or for a whole practice area;
//   - the "Example of fully in place" line is calibration, not a requirement
//     (C1.3) — it is shown as an example, under the question.
//
// Save-and-resume is how writes work: every response is persisted the moment
// it is given (ensure-on-first-edit, then patch). A justification is saved
// when the respondent saves it, never on each keystroke.
//
// What the respondent never sees: evidence, triggers, adjudication, findings,
// the register, other runs, or system error text (DISCLOSURE_POLICY V1.1,
// category 5 — every failure goes through clientError).

interface Area {
  block: InstrumentBlock;
  questions: InstrumentCriterion[];
}

type Loaded = {
  run: Run;
  areas: Area[];
  levels: Map<string, ScaleLevel[]>;
  blockResponses: Map<string, BlockResponse>;          // by block key
  criterionResponses: Map<string, CriterionResponse>;  // by block key + criterion key
};

type State =
  | { kind: "loading" }
  | { kind: "none" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: Loaded };

type CriterionPatch = Parameters<typeof updateCriterionResponse>[1];

const ck = (blockKey: string, criterionKey: string) => `${blockKey}\u0000${criterionKey}`;
const hasText = (t: string | null | undefined) => (t ?? "").trim().length > 0;

// PRIVACY_DOCUMENTATION_FIRST_CLIENT 240926 V1.1 §8, G-P5 (approved): free
// text cannot be pseudonymised, so the respondent is asked not to put other
// people's names into it. Shown under every justification field.
export const NO_NAMES_HINT = "Please don't include people's names.";

// DESIGN_SLICE_SPONSOR_DEFINITION 011126 V1.0: the first respondent was unsure
// in three answers whether "executive sponsor" meant the programme owner or the
// programme manager. Wording approved by the content owner (Kenneth,
// 2026-11-01, "Yes, as written"); shown once, at the top of the questionnaire.
export const SPONSOR_DEFINITION =
  "means the senior leader who commissioned the PMO and agrees its mandate. In a programme, this is usually the programme owner, not the programme manager.";

export function RespondAssessment() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [areaIndex, setAreaIndex] = useState(0);
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Justification drafts, keyed like responses ("area:<block>" for a whole area).
  const [drafts, setDrafts] = useState<Map<string, string>>(new Map());
  const [naOpen, setNaOpen] = useState<Set<string>>(new Set());
  // One creation per practice area, however fast the answers come: two quick
  // answers in a new area would otherwise both create the area's block row,
  // and the second would fail on the (run, block) unique key.
  const blockInFlight = useRef(new Map<string, Promise<BlockResponse>>());

  const load = useCallback(async () => {
    try {
      const mine = currentAssessment(await listMyAssignedRuns());
      if (!mine) {
        setState({ kind: "none" });
        return;
      }
      const run = await getRun(mine.run_id);
      if (!run) {
        setState({ kind: "none" });
        return;
      }
      const v = run.instrument_version_id;
      const [blocks, criteria, levels, blockRows, critRows] = await Promise.all([
        listInstrumentBlocks(v),
        listInstrumentCriteria(v),
        listScaleAnchors(v),
        listBlockResponses(run.id),
        listCriterionResponses(run.id),
      ]);
      const areas: Area[] = blocks
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .map((block) => ({
          block,
          questions: criteria
            .filter((c) => c.instrument_block_id === block.id)
            .sort((a, b) => a.sequence - b.sequence),
        }));
      const blockResponses = new Map(blockRows.map((r) => [r.block_id, r]));
      const blockKeyOfResponse = new Map(blockRows.map((r) => [r.id, r.block_id]));
      const criterionResponses = new Map<string, CriterionResponse>();
      for (const r of critRows) {
        const bk = blockKeyOfResponse.get(r.block_response_id);
        if (bk) criterionResponses.set(ck(bk, r.criterion_id), r);
      }
      setState({ kind: "ready", data: { run, areas, levels, blockResponses, criterionResponses } });
    } catch (e) {
      setState({ kind: "error", message: clientError("load", e).message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const prog = useMemo(() => {
    if (state.kind !== "ready") return null;
    const { areas, criterionResponses, blockResponses } = state.data;
    const questions = areas.flatMap((a) =>
      a.questions.map((q) => ({ block_key: a.block.block_key, criterion_key: q.criterion_key })),
    );
    const answers = questions.flatMap((q) => {
      const r = criterionResponses.get(ck(q.block_key, q.criterion_key));
      return r ? [{ ...q, applicability: r.applicability, score: r.score, rationale: r.rationale }] : [];
    });
    const areaResponses = [...blockResponses.values()].map((r) => ({
      block_key: r.block_id,
      applicability: r.applicability,
      rationale: r.rationale,
    }));
    return progress(questions, answers, areaResponses);
  }, [state]);

  // Resume where the respondent left off, once per visit: on the first load
  // only, so answering never moves them away from the area they are in.
  const resumed = useRef(false);
  useEffect(() => {
    if (!prog || resumed.current) return;
    resumed.current = true;
    setAreaIndex(resumeAreaIndex(prog));
  }, [prog]);

  // Move on when an answer completes the area the respondent is in (journey
  // slice 6): only on the change from incomplete to complete within the same
  // area, never on arrival or on changing an answer in a finished area. The
  // pause lets them see their choice before the next area opens at the top.
  const lastSeen = useRef<{ index: number; complete: boolean } | null>(null);
  useEffect(() => {
    if (!prog || !resumed.current) return;
    const cur = prog.areas[areaIndex];
    if (!cur) return;
    const before = lastSeen.current;
    lastSeen.current = { index: areaIndex, complete: cur.complete };
    if (!before || before.index !== areaIndex || before.complete || !cur.complete) return;
    const next = areaAfterCompleting(prog, areaIndex);
    if (next === areaIndex) return;
    const timer = window.setTimeout(() => {
      setAreaIndex(next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [prog, areaIndex]);

  const withSaving = useCallback(async (key: string, work: () => Promise<void>) => {
    setSaving((s) => new Set(s).add(key));
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(clientError("save", e).message);
    } finally {
      setSaving((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  }, []);

  const ensureBlock = useCallback(
    async (data: Loaded, blockKey: string): Promise<BlockResponse> => {
      const existing = data.blockResponses.get(blockKey);
      if (existing) return existing;
      let pending = blockInFlight.current.get(blockKey);
      if (!pending) {
        pending = createBlockResponse(data.run.organisation_id, data.run.id, blockKey);
        blockInFlight.current.set(blockKey, pending);
        pending.catch(() => blockInFlight.current.delete(blockKey));
      }
      return pending;
    },
    [],
  );

  const patchQuestion = useCallback(
    async (blockKey: string, criterionKey: string, patch: CriterionPatch) => {
      if (state.kind !== "ready") return;
      const key = ck(blockKey, criterionKey);
      if (saving.has(key)) return;           // one write per question at a time
      const data = state.data;
      await withSaving(key, async () => {
        const block = await ensureBlock(data, blockKey);
        let crit = data.criterionResponses.get(key);
        if (!crit) crit = await createCriterionResponse(data.run.organisation_id, data.run.id, block.id, criterionKey);
        const updated = await updateCriterionResponse(crit.id, patch);
        setState((prev) => {
          if (prev.kind !== "ready") return prev;
          const nextB = new Map(prev.data.blockResponses);
          if (!nextB.has(blockKey)) nextB.set(blockKey, block);
          const nextC = new Map(prev.data.criterionResponses);
          nextC.set(key, updated);
          return { kind: "ready", data: { ...prev.data, blockResponses: nextB, criterionResponses: nextC } };
        });
      });
    },
    [state, saving, withSaving, ensureBlock],
  );

  /** The whole area not applicable (with a reason), or applicable again. */
  const setAreaApplicability = useCallback(
    async (area: Area, notApplicable: boolean, reason: string | null) => {
      if (state.kind !== "ready") return;
      const blockKey = area.block.block_key;
      const key = `area:${blockKey}`;
      const data = state.data;
      await withSaving(key, async () => {
        const block = await ensureBlock(data, blockKey);
        // Answers under an N/A area are an evaluation error ("a score is
        // recorded but the effective applicability is 'not_applicable'"), so
        // they are cleared first. The respondent was told this before
        // confirming.
        const cleared = new Map<string, CriterionResponse>();
        if (notApplicable) {
          for (const q of area.questions) {
            const existing = data.criterionResponses.get(ck(blockKey, q.criterion_key));
            if (existing && (existing.score !== null || existing.applicability !== "assessed" || existing.rationale)) {
              cleared.set(
                ck(blockKey, q.criterion_key),
                await updateCriterionResponse(existing.id, { applicability: "assessed", score: null, rationale: null }),
              );
            }
          }
        }
        const updated = await updateBlockResponse(block.id, {
          applicability: notApplicable ? "not_applicable" : "assessed",
          rationale: notApplicable ? reason : null,
        });
        setState((prev) => {
          if (prev.kind !== "ready") return prev;
          const nextB = new Map(prev.data.blockResponses);
          nextB.set(blockKey, updated);
          const nextC = new Map(prev.data.criterionResponses);
          for (const [k, v] of cleared) nextC.set(k, v);
          return { kind: "ready", data: { ...prev.data, blockResponses: nextB, criterionResponses: nextC } };
        });
      });
    },
    [state, withSaving, ensureBlock],
  );

  const submit = useCallback(async () => {
    if (state.kind !== "ready") return;
    setSubmitting(true);
    setError(null);
    try {
      await submitSelfAssessment(state.data.run.id, state.data.run.state);
      navigate("/respond/report");
    } catch (e) {
      setError(clientError("submit", e).message);
      setSubmitting(false);
      setConfirming(false);
      await load();                          // show the state the database holds
    }
  }, [state, navigate, load]);

  const draft = (k: string, fallback: string | null) => drafts.get(k) ?? fallback ?? "";
  const setDraft = (k: string, v: string) => setDrafts((d) => new Map(d).set(k, v));
  const toggleOpen = (k: string, open: boolean) =>
    setNaOpen((s) => {
      const n = new Set(s);
      if (open) n.add(k);
      else n.delete(k);
      return n;
    });

  if (state.kind === "loading") return <p>Loading your assessment…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;
  if (state.kind === "none") {
    return (
      <div className="card empty-state">
        <h3>No assessment is waiting for you</h3>
        <p>If you expected one, please contact the person at LINNFOSS who invited you.</p>
      </div>
    );
  }

  const { run, areas, levels, criterionResponses, blockResponses } = state.data;

  if (isSubmitted(run.state)) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Your assessment has been submitted</h2>
        <p>Thank you. Your answers are recorded and can no longer be changed.</p>
        <Link className="btn" to="/respond/report">Open your report</Link>
      </div>
    );
  }
  if (!isAnswering(run.state)) {
    return (
      <div className="card empty-state">
        <h3>This assessment is closed</h3>
        <p>It can no longer be answered. Please contact the person at LINNFOSS who invited you.</p>
      </div>
    );
  }
  if (areas.length === 0 || !prog) {
    return <div className="error-banner">This assessment has no questions. Please contact LINNFOSS.</div>;
  }

  const index = Math.min(areaIndex, areas.length - 1);
  const area = areas[index];
  const areaProg = prog.areas[index];
  if (!area || !areaProg) {
    return <div className="error-banner">This practice area could not be shown. Please reload the page.</div>;
  }
  const areaKey = `area:${area.block.block_key}`;
  const areaResp = blockResponses.get(area.block.block_key);
  const areaIsNA = areaResp?.applicability === "not_applicable";
  const areaBusy = saving.has(areaKey);

  const optionStyle = (selected: boolean, busy: boolean) => ({
    display: "flex",
    gap: "0.6rem",
    alignItems: "flex-start",
    padding: "0.45rem 0.6rem",
    borderRadius: "var(--radius)",
    border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
    background: selected ? "var(--accent-soft)" : "var(--surface-card)",
    cursor: busy ? "wait" : "pointer",
  });

  return (
    <div>
      <h1 style={{ marginBottom: "0.25rem" }}>PMO Self-Assessment</h1>
      <p style={{ color: "var(--text-secondary)", marginTop: 0 }}>
        {prog.answered} of {prog.total} answered · saved as you go
      </p>
      <p style={{ fontSize: "var(--lf-body-size)", color: "var(--text-secondary)" }}>
        <em>Executive sponsor</em> {SPONSOR_DEFINITION}
      </p>

      {error && <div className="error-banner" role="alert">{error}</div>}

      <nav aria-label="Practice areas" style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", margin: "1rem 0" }}>
        {areas.map((a, i) => {
          const p = prog.areas[i] ?? { answered: 0, total: a.questions.length, complete: false, notApplicable: false };
          return (
            <button
              key={a.block.id}
              className={i === index ? "btn" : "btn btn-secondary"}
              style={{ fontSize: "var(--lf-meta-size)", padding: "0.3rem 0.55rem" }}
              aria-current={i === index ? "step" : undefined}
              title={`${a.block.title} — ${p.notApplicable ? "not applicable" : `${p.answered} of ${p.total} answered`}`}
              onClick={() => setAreaIndex(i)}
            >
              {p.complete ? "✓ " : ""}{i + 1}
            </button>
          );
        })}
      </nav>

      <section className="card survey-card">
        <p style={{ margin: 0, fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
          Practice area {index + 1} of {areas.length} ·{" "}
          {areaIsNA ? "not applicable" : `${areaProg.answered} of ${areaProg.total} answered`}
        </p>
        <h2 style={{ marginTop: "0.25rem" }}>{area.block.title}</h2>
        {area.block.purpose && <p style={{ color: "var(--text-secondary)" }}>{area.block.purpose}</p>}

        {/* The whole area not applicable (C1.1) */}
        <div style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius)", padding: "0.6rem 0.8rem", marginBottom: "0.5rem" }}>
          {areaIsNA ? (
            <>
              <p style={{ margin: 0 }}>
                <strong>You have marked this practice area as not applicable to your PMO.</strong>
              </p>
              <p style={{ margin: "0.3rem 0", color: "var(--text-secondary)" }}>Your reason: {areaResp?.rationale}</p>
              <button className="btn btn-secondary" disabled={areaBusy || submitting} onClick={() => void setAreaApplicability(area, false, null)}>
                This area does apply after all
              </button>
            </>
          ) : naOpen.has(areaKey) ? (
            <>
              <label htmlFor={`${areaKey}-reason`} style={{ fontWeight: 600 }}>
                Why does this practice area not apply to your PMO?
              </label>
              <textarea
                id={`${areaKey}-reason`}
                rows={2}
                style={{ width: "100%", marginTop: "0.3rem" }}
                value={draft(areaKey, null)}
                onChange={(e) => setDraft(areaKey, e.target.value)}
              />
              <p style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: "0.3rem 0" }}>
                {NO_NAMES_HINT} Any answers you have given in this area will be cleared.
              </p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn"
                  disabled={!hasText(draft(areaKey, null)) || areaBusy || submitting}
                  onClick={() => {
                    void setAreaApplicability(area, true, draft(areaKey, null).trim()).then(() => toggleOpen(areaKey, false));
                  }}
                >
                  Mark area not applicable
                </button>
                <button className="btn btn-secondary" onClick={() => toggleOpen(areaKey, false)}>Cancel</button>
              </div>
            </>
          ) : (
            <button className="btn btn-secondary" style={{ fontSize: "var(--lf-body-size)" }} disabled={submitting} onClick={() => toggleOpen(areaKey, true)}>
              This practice area does not apply to our PMO…
            </button>
          )}
        </div>

        {!areaIsNA &&
          area.questions.map((q, qi) => {
            const key = ck(area.block.block_key, q.criterion_key);
            const current = criterionResponses.get(key);
            const scale = levels.get(q.scale) ?? [];
            const busy = saving.has(key);
            const chosenScore = current?.applicability === "assessed" ? current.score : null;
            const isUnable = current?.applicability === "not_assessed";
            const isNA = current?.applicability === "not_applicable";
            const naEditing = naOpen.has(key) || (isNA && !hasText(current?.rationale));
            const maxScore = scale.reduce((m, lv) => Math.max(m, lv.value), Number.NEGATIVE_INFINITY);
            const reasonOffered = current?.applicability === "assessed" && offersReason(chosenScore, maxScore);
            const reasonKey = `${key}:why`;
            const reasonText = draft(reasonKey, current?.rationale ?? null);
            const reasonDirty = storedReason(reasonText) !== (current?.rationale ?? null);
            return (
              <fieldset key={q.id} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "0.9rem 1rem", margin: "1rem 0" }}>
                <legend style={{ fontSize: "var(--lf-item-size)", lineHeight: "var(--lf-item-line)", fontWeight: 600, padding: "0 0.3rem" }}>
                  {qi + 1}. {q.perspective}
                </legend>
                <p style={{ marginTop: 0 }}>{q.required_content}</p>
                {q.how_tested && <p style={{ fontSize: "var(--lf-body-size)", color: "var(--text-muted)" }}>{q.how_tested}</p>}
                <div role="radiogroup" aria-label={q.perspective ?? q.criterion_key} style={{ display: "grid", gap: "0.4rem" }}>
                  {scale.map((lv) => (
                    <label key={lv.value} style={optionStyle(chosenScore === lv.value, busy)}>
                      <input
                        type="radio"
                        name={key}
                        checked={chosenScore === lv.value}
                        disabled={busy || submitting}
                        onChange={() => {
                          toggleOpen(key, false);
                          // A reason given for a lower answer stays with the question;
                          // the top level carries none (DESIGN_SLICE_ANSWER_REASON).
                          const keep = current?.applicability === "assessed" && offersReason(lv.value, maxScore) ? (current.rationale ?? null) : null;
                          void patchQuestion(area.block.block_key, q.criterion_key, { applicability: "assessed", score: lv.value, rationale: keep });
                        }}
                      />
                      <span>
                        <strong>{lv.state}</strong>
                        <span style={{ display: "block", fontSize: "var(--lf-body-size)", color: "var(--text-secondary)" }}>{lv.anchor}</span>
                      </span>
                    </label>
                  ))}
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.2rem" }}>
                    <label style={{ ...optionStyle(isUnable, busy), flex: "1 1 14rem" }}>
                      <input
                        type="radio"
                        name={key}
                        checked={isUnable}
                        disabled={busy || submitting}
                        onChange={() => {
                          toggleOpen(key, false);
                          void patchQuestion(area.block.block_key, q.criterion_key, { applicability: "not_assessed", score: null, rationale: null });
                        }}
                      />
                      <span>
                        <strong>{UNABLE_TO_ASSESS}</strong>
                        <span style={{ display: "block", fontSize: "var(--lf-body-size)", color: "var(--text-secondary)" }}>
                          I cannot tell from the information available to me.
                        </span>
                      </span>
                    </label>
                    <label style={{ ...optionStyle(isNA || naOpen.has(key), busy), flex: "1 1 14rem" }}>
                      <input
                        type="radio"
                        name={key}
                        checked={isNA || naOpen.has(key)}
                        disabled={busy || submitting}
                        onChange={() => toggleOpen(key, true)}
                      />
                      <span>
                        <strong>{NOT_APPLICABLE}</strong>
                        <span style={{ display: "block", fontSize: "var(--lf-body-size)", color: "var(--text-secondary)" }}>
                          This practice is outside our PMO's mandate. A short reason is needed.
                        </span>
                      </span>
                    </label>
                  </div>
                  {reasonOffered && (
                    <div style={{ marginTop: "0.4rem", display: "grid", gap: "0.3rem" }}>
                      <label htmlFor={`${reasonKey}-t`} style={{ fontSize: "var(--lf-body-size)", fontWeight: 600 }}>
                        Why? (optional)
                      </label>
                      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                        <textarea
                          id={`${reasonKey}-t`}
                          rows={2}
                          value={reasonText}
                          placeholder="In your own words"
                          disabled={busy || submitting}
                          onChange={(e) => setDraft(reasonKey, e.target.value)}
                          style={{ flex: "1 1 16rem" }}
                        />
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "var(--lf-body-size)" }}
                          disabled={!reasonDirty || busy || submitting}
                          onClick={() => {
                            void patchQuestion(area.block.block_key, q.criterion_key, {
                              applicability: "assessed",
                              score: chosenScore,
                              rationale: storedReason(reasonText),
                            });
                          }}
                        >
                          Save reason
                        </button>
                      </div>
                      <p style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: 0 }}>{NO_NAMES_HINT}</p>
                    </div>
                  )}
                  {isNA && !naEditing && (
                    <p style={{ fontSize: "var(--lf-body-size)", margin: "0.2rem 0 0" }}>
                      Your reason: {current?.rationale}{" "}
                      <button className="btn btn-secondary" style={{ fontSize: "var(--lf-meta-size)", padding: "0.1rem 0.4rem" }} onClick={() => toggleOpen(key, true)}>
                        Edit
                      </button>
                    </p>
                  )}
                  {naEditing && (
                    <div>
                      <label htmlFor={`${key}-reason`} style={{ fontSize: "var(--lf-body-size)", fontWeight: 600 }}>
                        Why does this practice not apply to your PMO?
                      </label>
                      <textarea
                        id={`${key}-reason`}
                        rows={2}
                        style={{ width: "100%", marginTop: "0.25rem" }}
                        value={draft(key, current?.rationale ?? null)}
                        onChange={(e) => setDraft(key, e.target.value)}
                      />
                      <p style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: "0.2rem 0" }}>{NO_NAMES_HINT}</p>
                      <button
                        className="btn"
                        style={{ marginTop: "0.3rem" }}
                        disabled={!hasText(draft(key, current?.rationale ?? null)) || busy || submitting}
                        onClick={() => {
                          const reason = draft(key, current?.rationale ?? null).trim();
                          void patchQuestion(area.block.block_key, q.criterion_key, {
                            applicability: "not_applicable",
                            score: null,
                            rationale: reason,
                          }).then(() => toggleOpen(key, false));
                        }}
                      >
                        Save reason
                      </button>
                    </div>
                  )}
                </div>
                {/* Always in the layout, shown only while saving: a line that came
                    and went pushed the page down and back on every click. */}
                <p
                  aria-live="polite"
                  style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: "0.4rem 0 0", visibility: busy ? "visible" : "hidden" }}
                >
                  Saving…
                </p>
              </fieldset>
            );
          })}

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "space-between" }}>
          <button className="btn btn-secondary" disabled={index === 0} onClick={() => setAreaIndex(index - 1)}>
            Previous area
          </button>
          {index < areas.length - 1 ? (
            <button className="btn" onClick={() => setAreaIndex(index + 1)}>Next area</button>
          ) : (
            <button className="btn" disabled={!prog.canSubmit || submitting} onClick={() => setConfirming(true)}>
              Review and submit
            </button>
          )}
        </div>
      </section>

      {index === areas.length - 1 && !prog.canSubmit && (
        <p style={{ color: "var(--text-secondary)" }}>
          Every question needs a response before you can submit — an answer, <em>{UNABLE_TO_ASSESS}</em>, or{" "}
          <em>{NOT_APPLICABLE}</em> with a reason. Areas still open:{" "}
          {prog.areas
            .map((p, i) => (p.complete ? null : i + 1))
            .filter((n) => n !== null)
            .join(", ")}
          .
        </p>
      )}

      {confirming && (
        <div className="card" role="dialog" aria-modal="true" aria-labelledby="confirm-title" style={{ borderColor: "var(--accent)" }}>
          <h3 id="confirm-title" style={{ marginTop: 0 }}>Submit your assessment?</h3>
          <p>
            All {prog.total} questions have a response. <strong>After you submit, your answers can no longer be
            changed.</strong> Your report is produced straight away.
          </p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="btn" disabled={submitting} onClick={() => void submit()}>
              {submitting ? "Submitting…" : "Submit"}
            </button>
            <button className="btn btn-secondary" disabled={submitting} onClick={() => setConfirming(false)}>
              Go back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
